import {
  addDays,
  type Duration,
  endOfWeek as dateFnsEndOfWeek,
  isToday as dateFnsIsToday,
  startOfWeek as dateFnsStartOfWeek,
  endOfDay,
  type FormatDistanceFnOptions,
  type FormatDistanceToken,
  type FormatDistanceToNowStrictOptions,
  format,
  formatDistanceStrict,
  intervalToDuration,
  isBefore,
  isSameDay,
  isSameHour,
  isSameMinute,
  isSameMonth,
  isSameYear,
  isWeekend,
  isWithinInterval,
  parse,
  parseISO,
  startOfDay,
} from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import { z } from 'zod'
import { DEFAULT_CLINIC_TIME_ZONE } from '#src/addressTimezone.ts'
import { now } from '#src/utils.ts'

export function getDateTimePeriodDisplay(props: {
  start: string | Date | null | undefined
  end: string | Date | null | undefined
  precision?: 'year' | 'month' | 'day' | 'hour' | 'minute'
}): string {
  const { start, end, precision = 'minute' } = props
  if (!start || !end) {
    return ''
  }

  const startDate = new Date(start)
  const endDate = new Date(end)

  const formatString = getFormatString(precision)
  let formattedPeriod: string

  if (areDatesEqualAtPrecision(startDate, endDate, precision)) {
    formattedPeriod = format(startDate, formatString)
  } else if (isSameYear(startDate, endDate)) {
    if (isSameMonth(startDate, endDate)) {
      if (isSameDay(startDate, endDate)) {
        formattedPeriod = `${format(startDate, formatString)} - ${format(endDate, getTimeFormatString(precision))}`
      } else {
        formattedPeriod = `${format(startDate, formatString)} - ${format(endDate, formatString)}`
      }
    } else {
      formattedPeriod = `${format(startDate, 'MMM')} - ${format(endDate, 'MMM yyyy')}`
    }
  } else {
    formattedPeriod = `${format(startDate, 'MMM yyyy')} - ${format(endDate, 'MMM yyyy')}`
  }

  return formattedPeriod
}

function getFormatString(precision: string): string {
  switch (precision) {
    case 'year':
      return 'yyyy'
    case 'month':
      return 'MMM yyyy'
    case 'day':
      return 'MM/dd/yy'
    default:
      return 'MM/dd/yy, h:mm a'
  }
}

function getTimeFormatString(precision: string): string {
  switch (precision) {
    case 'year':
    case 'month':
    case 'day':
      return ''
    default:
      return 'h:mm a'
  }
}

function areDatesEqualAtPrecision(
  date1: Date,
  date2: Date,
  precision: string
): boolean {
  switch (precision) {
    case 'year':
      return isSameYear(date1, date2)
    case 'month':
      return isSameYear(date1, date2) && isSameMonth(date1, date2)
    case 'day':
      return isSameDay(date1, date2)
    case 'hour':
      return isSameDay(date1, date2) && isSameHour(date1, date2)
    default:
      return isSameMinute(date1, date2)
  }
}

export function getNextNWorkdays(n: number, startDate?: Date | string): Date[] {
  const days: Date[] = []

  let day = startOfDay(startDate ?? new Date())
  while (days.length < n) {
    if (!isWorkday(day)) {
      day = addDays(day, 1)
      continue
    }
    days.push(day)
    day = addDays(day, 1)
  }

  return days
}

export function getNextWorkday(from: Date | string = now()): Date {
  let nextDate = addDays(from, 1)
  while (!isWorkday(nextDate)) {
    nextDate = addDays(nextDate, 1)
  }
  return nextDate
}

export function isWorkday(date: Date | string): boolean {
  // TODO: consider adding holidays
  return !isWeekend(date)
}

export const dateStringSchema = z.string().date() // ISO date format (YYYY-MM-DD)
export type DateString = z.infer<typeof dateStringSchema> // ISO date format (YYYY-MM-DD)

export function isDateString(date: string): boolean {
  return dateStringSchema.safeParse(date).success
}

export function toDateString(date: Date | string | number): DateString {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return format(parsedDate, 'yyyy-MM-dd')
}

export function parseDateString(dateString: DateString): Date {
  return parseISO(dateString)
}

export function formatDate(date: DateString | Date): string {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  if (isSameYear(parsedDate, new Date())) {
    return format(parsedDate, 'MMM d')
  }
  return format(parsedDate, 'MMM d, yyyy')
}

export function formatDateNumber(date: DateString | Date): string {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return format(parsedDate, 'MM/dd/yyyy')
}

export function formatDateNumberShort(date: DateString | Date): string {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return format(parsedDate, 'MM/dd/yy')
}

// The clinic's calendar day for an instant (now by default): the same day in
// the browser and on the server, whatever their time zones.
export function clinicDay(instant: Date | string = new Date()): DateString {
  return formatInTimeZone(
    instant,
    DEFAULT_CLINIC_TIME_ZONE,
    'yyyy-MM-dd'
  ) as DateString
}

export function today(): DateString {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function dateStringToDate(dateString: DateString): Date {
  const [year, month, day] = dateString.split('-')
  return new Date(Number(year), Number(month) - 1, Number(day))
}

function dateStringToDateInUTC(dateString: DateString): Date {
  const [year, month, day] = dateString.split('-')
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
}

export function isToday(dateString: DateString): boolean {
  return dateFnsIsToday(dateStringToDate(dateString))
}

export function isInNextNDays(dateString: DateString, days: number): boolean {
  return isWithinInterval(dateString, {
    start: today(),
    end: addDays(today(), days),
  })
}

export function isInPast(dateString: DateString): boolean {
  return isBefore(dateString, today())
}

export const datetimeStringSchema = z.string().datetime() // ISO 8601; by default only `Z` timezone allowed
export type DatetimeString = z.infer<typeof datetimeStringSchema> // ISO 8601; by default only `Z` timezone allowed

export function startOfDateInLocalTimezone(date: DateString): DatetimeString {
  return startOfDay(dateStringToDate(date)).toISOString()
}

/**
 * @param date - DateString or DatetimeString
 */
export function startOfDateInUTC(date: string): DatetimeString {
  if (isDateString(date)) {
    return dateStringToDateInUTC(date).toISOString()
  }
  const dateObject = parseISO(date)
  return new Date(
    Date.UTC(
      dateObject.getUTCFullYear(),
      dateObject.getUTCMonth(),
      dateObject.getUTCDate()
    )
  ).toISOString()
}

export function endOfDateInLocalTimezone(date: DateString): DatetimeString {
  return endOfDay(dateStringToDate(date)).toISOString()
}

export function startOfZonedDay(
  date: Date | string,
  timeZone: string
): DatetimeString {
  return fromZonedTime(
    startOfDay(
      toZonedTime(typeof date === 'string' ? parseISO(date) : date, timeZone)
    ),
    timeZone
  ).toISOString()
}

export function endOfZonedDay(
  date: Date | string,
  timeZone: string
): DatetimeString {
  return fromZonedTime(
    endOfDay(
      toZonedTime(typeof date === 'string' ? parseISO(date) : date, timeZone)
    ),
    timeZone
  ).toISOString()
}

export function formatTime(date: string | Date): string {
  return format(date, 'h:mm a')
}

export function formatDateTime(date: string | Date): string {
  return format(date, 'MM/dd/yy, h:mm a')
}

export function formatDateTimePrecise(date: string | Date): string {
  return format(date, 'MMM d, yyyy, h:mm:ss a')
}

export function startOfWeek(date: string | Date): Date {
  return dateFnsStartOfWeek(typeof date === 'string' ? parseISO(date) : date, {
    weekStartsOn: 0,
  })
}

export function endOfWeek(date: string | Date): Date {
  return dateFnsEndOfWeek(typeof date === 'string' ? parseISO(date) : date, {
    weekStartsOn: 0,
  })
}

/**
 * Parse a time string format into a date
 *
 * @param value - The string to parse (e.g. "14:30")
 * @param referenceDate - The reference date to use for date values (defaults to current date)
 * @returns A Date object representing the parsed time
 */
export function parseTime(
  value: string,
  referenceDate: Date = new Date()
): Date {
  return parse(value, 'HH:mm', referenceDate)
}

export function formatRelativeDateShort(
  date: Date | string,
  toDate?: Date | string
): string {
  const duration = intervalToDuration({
    start: date,
    end: toDate ?? new Date(),
  })
  return formatDurationShort(duration)
}

export function formatDurationShort(duration: Duration): string {
  if (duration.years) {
    return `${duration.years}y`
  }
  if (duration.months) {
    return `${duration.months}mo`
  }
  if (duration.weeks) {
    return `${duration.weeks}w`
  }
  if (duration.days) {
    return `${duration.days}d`
  }
  if (duration.hours) {
    return `${duration.hours}h`
  }
  if (duration.minutes) {
    return `${duration.minutes}m`
  }
  return 'just now'
}

const formatDistanceLocale: Record<
  FormatDistanceToken,
  (count: number, opts?: FormatDistanceFnOptions) => string
> = {
  lessThanXSeconds: (_c, opts) =>
    `${opts?.addSuffix ? 'in ' : ''}less than a min`,
  xSeconds: (_c, opts) => `${opts?.addSuffix ? 'in ' : ''}less than a min`,
  halfAMinute: (_c, opts) => `${opts?.addSuffix ? 'in ' : ''}less than a min`,
  lessThanXMinutes: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}m`,
  xMinutes: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}m`,
  aboutXHours: (c, opts) => `${opts?.addSuffix ? 'in about ' : ''}${c}h`,
  xHours: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}h`,
  xDays: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}d`,
  aboutXWeeks: (c, opts) => `${opts?.addSuffix ? 'in about ' : ''}${c}w`,
  xWeeks: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}w`,
  aboutXMonths: (c, opts) => `${opts?.addSuffix ? 'in about ' : ''}${c}m`,
  xMonths: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}m`,
  aboutXYears: (c, opts) => `${opts?.addSuffix ? 'in about ' : ''}${c}y`,
  xYears: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}y`,
  overXYears: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}y`,
  almostXYears: (c, opts) => `${opts?.addSuffix ? 'in ' : ''}${c}y`,
}

export function formatDistance(
  date: Date | string,
  baseDate: Date | string,
  options?: FormatDistanceToNowStrictOptions
): string {
  return formatDistanceStrict(date, baseDate, {
    locale: {
      formatDistance: (token, count, opts) =>
        formatDistanceLocale[token](count, opts),
    },
    ...options,
  })
}
